import { isEmpty, pickBy } from 'lodash-es';
import {
  Listing,
  RowInfo,
  SectionInfo,
  SelectedSectionSettings,
} from 'src/WebApiController';

export const sortRowsByOrdinal = (rows: RowInfo[]) =>
  rows
    .filter(({ ordinal, isSpeculative }) => ordinal != null && !isSpeculative)
    .toSorted((a, b) => a.ordinal! - b.ordinal!);

export const onSectionSelctionWithMyRowDefaults = (
  compListingSettings: SelectedSectionSettings | null,
  selectedSections: SectionInfo[],
  venueMapSections: SectionInfo[],
  listing?: Listing
): SelectedSectionSettings => {
  let rowIdFilter = compListingSettings?.rowIdFilter ?? [];
  let sectionRowIdFilter = compListingSettings?.sectionRowIdFilter ?? {};

  const sectionIdFilter = selectedSections.map((s) => s.id);

  const rowOffset = null;
  // Happy case when no row id filters on all sections, default to `My Row` (rowOffset = 0)
  if (isEmpty(sectionRowIdFilter) || isEmpty(rowIdFilter) || rowOffset === 0) {
    // Set rowOffset to null when no section selected
    const rowOffset = isEmpty(sectionIdFilter) ? null : 0;
    return {
      ...compListingSettings,
      sectionIdFilter,
      sectionRowIdFilter: {},
      rowIdFilter: [],
      rowOffset,
    } as SelectedSectionSettings;
  }

  // Important sanitize SectionRowIdFilter and RowIdFilter to be aligned with sectionid filters
  sectionRowIdFilter = pickBy(
    Object.assign({}, sectionRowIdFilter ?? {}),
    (_, key) => sectionIdFilter.includes(parseInt(key))
  );
  const selectedSectionRowIds = selectedSections.flatMap(({ rows }) =>
    rows.map(({ id }) => id)
  );
  rowIdFilter = rowIdFilter.filter((id) => selectedSectionRowIds.includes(id));

  // Bulk or group settings with existing row id filters, only update the sectionIdFilter
  // which will count in all the rows in the section
  if (!listing) {
    return {
      ...compListingSettings,
      sectionIdFilter,
      sectionRowIdFilter,
      rowIdFilter,
      rowOffset: null,
    } as SelectedSectionSettings;
  }

  const listingSection = venueMapSections.find(
    ({ id }) => id === listing.seating.sectionId
  );
  const sectionsCanBeDefault = selectedSections.filter(
    ({ id }) => sectionRowIdFilter[id] == null
  );
  // No matching listing section => this should not happen
  // Or all sections have row id fitlers => don't default with `My Row`
  if (!listingSection || !sectionsCanBeDefault?.length) {
    return {
      ...compListingSettings,
      sectionRowIdFilter,
      sectionIdFilter,
      rowIdFilter,
      rowOffset: null,
    } as SelectedSectionSettings;
  }

  const sortedSourceRows = sortRowsByOrdinal(listingSection.rows);
  // the ordinal ranking position of the selected row against all the rows
  const rankedOrdinalIndex = sortedSourceRows.findIndex(
    ({ id }) => listing.seating.rowId === id
  );

  for (const section of sectionsCanBeDefault) {
    // Remove all the row filters associated with the section id
    const sectionRowIds = section.rows.map((r) => r.id);
    rowIdFilter = [
      ...(rowIdFilter ?? []).filter((rowId) => !sectionRowIds.includes(rowId)),
    ];

    // get matched row from id or name
    const matchingRow = section.rows.find(
      ({ id, name }) =>
        id === listing.seating.rowId ||
        name?.toLowerCase() === (listing.seating.row ?? '').toLowerCase()
    );

    if (matchingRow !== undefined) {
      // Only store the highest ordinal row id to display in the pill
      sectionRowIdFilter[section.id] = matchingRow.id.toString();

      // Exclude all rows from section if the matching row has no ordinal
      if (matchingRow.ordinal != null) {
        // Push all the row ids with an ordinal less than or equal to the matching row
        section.rows.forEach((row) => {
          if (row.ordinal == null) return;
          if (row.ordinal <= matchingRow.ordinal!) {
            rowIdFilter.push(row.id);
          }
        });
      }
      continue;
    }

    // if no matching row, use row with closest lower oridinal
    if (rankedOrdinalIndex != null) {
      // row with closest low oridinal rank
      const sortedRows = sortRowsByOrdinal(section.rows);
      const filteredRows = sortedRows.filter(
        (_, index) => index <= rankedOrdinalIndex
      );

      if (filteredRows && filteredRows.length > 0) {
        const lastRow = filteredRows[filteredRows.length - 1];
        sectionRowIdFilter[section.id] = lastRow.id.toString();
        filteredRows.forEach((row) => rowIdFilter.push(row.id));
      }
      continue;
    }

    delete sectionRowIdFilter[section.id];
  }

  return {
    ...compListingSettings,
    sectionIdFilter,
    sectionRowIdFilter,
    rowIdFilter,
    rowOffset: null,
  } as SelectedSectionSettings;
};
